<template>
    <RedesignBaseModal
        :visible="isOpen"
        :width="MODAL_WIDTH"
        :title="String($t('general.deleteConfirmation'))"
        wrapper-classes="confirm-modal"
        append-to-body
        @close="closeModal"
    >
        <div class="w-full main-text-color">
            <div class="text-center mb-5">
                <div>{{ promptMessage }}</div>
                <div class="font-semibold">
                    {{ selectedLayout?.LayoutName }}
                </div>
            </div>
            <div
                v-if="selectedLayout?.LayoutID && dashboardsWithThisLayout.length"
                class="w-full"
            >
                <div class="w-full bg-warning text-white rounded px-4 py-2 relative flex items-center">
                    <i class="vc-icon-info icon-xl" />
                    <div class="px-2 text-xs">
                        <div class="font-semibold">
                            {{ $t('layout.thisLayoutConfigIsAlreadyUsed') }}
                        </div>
                        <div
                            class="pt-2 break-keep text-balance"
                            v-html="alertMessage"
                        />
                    </div>
                </div>
                <div class="w-full mt-5">
                    <RedesignFormLabel
                        icon="IconLayoutSelection"
                        :label="String($t('layout.layoutSelection'))"
                        class="mb-2"
                    />
                    <RedesignLayoutSelect
                        v-model="layoutToReplace"
                        :excluded="[selectedLayout.LayoutID]"
                        @on-change-layout="onChangeLayout"
                    />
                </div>
            </div>
        </div>
        <template #footer>
            <div class="w-full flex items-center justify-between">
                <RedesignBaseButton
                    type="outline"
                    color="secondary"
                    icon="X"
                    @click="closeModal"
                >
                    {{ $t('common.cancel') }}
                </RedesignBaseButton>
                <RedesignBaseButton
                    :loading="loading"
                    :disabled="isConfirmDisabled"
                    color="destructive"
                    icon="vc-icon-recycle-bin"
                    @click="confirm"
                >
                    {{ $t('common.delete') }}
                </RedesignBaseButton>
            </div>
        </template>
    </RedesignBaseModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import { ILayoutItem } from '@/types/dashboard'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import { statusDictionary } from '@/views/DashboardSettings/LayoutManagement/layout-management'
import i18n from '@/i18n'
import { ActiveAccountDashboards } from '@/composables/globalData/useDashoardsData'
import RedesignLayoutSelect from '@/components/RedesignComponents/Select/RedesignLayoutSelect.vue'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import { LayoutApi } from '@/api/layoutApi'
import { DashboardApi } from '@/api/dashboardApi'

const MODAL_WIDTH = '480px'
const statusToSet = 2

const emit = defineEmits<{
    (e: 'confirm', layoutId: number, newLayoutID: number): void
}>()

const loading = ref(false)
const isOpen = ref<boolean>(false)
const selectedLayout = ref<ILayoutItem | undefined>()
const layoutToReplace = ref<number | undefined>(undefined)
const layoutToReplaceData = ref<ILayoutItem | undefined>(undefined)

const promptMessage = computed(() => {
    const message = statusDictionary[statusToSet]
    return i18n.t('common.confirm.question', {
        action: i18n.t(message)
    })
})
const allDashboards = computed(() => {
    return ActiveAccountDashboards.value || []
})
const dashboardsWithThisLayout = computed(() => {
    if (!selectedLayout.value) {
        return []
    }
    const layoutId = selectedLayout.value?.LayoutID ?? -1
    return allDashboards.value.filter(dashboard => +dashboard.DashboardLayoutID === +layoutId)
})
const alertMessage = computed(() => {
    let dashboards = ''
    dashboardsWithThisLayout.value.forEach((dashboard) => {
        dashboards += `${dashboard.DashboardTitle}.. `
    })
    return `${i18n.t('dashboard.youMustSelectAnotherConfiguration')} <br> <strong> ${dashboards} </strong>`
})
const isConfirmDisabled = computed(() => {
    return !!(dashboardsWithThisLayout.value.length && !layoutToReplace.value)
})

function closeModal () {
    isOpen.value = false
    selectedLayout.value = undefined
    layoutToReplace.value = undefined
    layoutToReplaceData.value = undefined
}
function openModal (layoutData?: ILayoutItem) {
    if (!layoutData) {
        return
    }
    selectedLayout.value = { ...layoutData }
    isOpen.value = true
}
async function confirm () {
    if (!selectedLayout.value?.LayoutID) {
        return
    }
    try {
        loading.value = true
        const dashboards = dashboardsWithThisLayout.value
        const newLayoutID = layoutToReplace.value || 1
        for (const dashboard of dashboards) {
            dashboard.DashboardLayoutID = newLayoutID
            dashboard.DashboardLayout = layoutToReplaceData.value
            await DashboardApi.update(dashboard)
        }
        const layoutSettings = {
            ...selectedLayout.value,
            LayoutStatusID: statusToSet,
            LayoutStatusName: 'Deleted',
        }

        await LayoutApi.update(layoutSettings)
        emit('confirm', selectedLayout.value.LayoutID, newLayoutID)
        closeModal()
    } catch (e) {
        console.warn(e)
    } finally {
        loading.value = false
    }
}
function onChangeLayout (layout?: ILayoutItem) {
    layoutToReplaceData.value = layout ? { ...layout } : layout
}

defineExpose({
    open: openModal,
    close: closeModal
})
</script>
